import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BulkGameAccountsResult,
  BulkLinkGameAccountsInput,
  GameAccountDto,
  GameDto,
} from '@gc/shared';

import { api } from '@/lib/api';
import { Field, ghostPillClass, inputClass, pillClass } from './controls';
import { isOwnerOnlyRefusal } from './gameAccountForm';
import { toFieldErrors, useResource } from './useSettings';

/**
 * "Add game…" for the ticked accounts (owner, 2026-09-23): one title added to
 * all of them in one request — the thirty "(BO6)" accounts linked to Bo6 in
 * one go. Adds only; the server leaves an account that already owns the game
 * as it is and touches no other game. All or nothing: an account deleted
 * since the page loaded refuses the lot, and the page reloads to show why.
 */
export function GameAccountsLinkDialog({
  accounts,
  onClose,
  onDone,
}: {
  /** The ticked accounts, as the page lists them. */
  accounts: GameAccountDto[];
  onClose: () => void;
  onDone: (updated: number) => void;
}) {
  const { t } = useTranslation();
  const games = useResource<GameDto[]>('/settings/games');
  const [gameId, setGameId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const alreadyOwn = gameId
    ? accounts.filter((account) => account.gameIds.includes(gameId)).length
    : 0;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!gameId || accounts.length === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      const body: BulkLinkGameAccountsInput = {
        accountIds: accounts.map((account) => account.id),
        gameId,
      };
      const result = await api.post<BulkGameAccountsResult>(
        '/settings/game-accounts/bulk/link-game',
        body,
      );
      onDone(result.updated);
    } catch (caught) {
      setError(
        isOwnerOnlyRefusal(caught)
          ? t('settings.gameAccounts.ownerOnly')
          : (toFieldErrors(caught, t('gameAccountTools.link.failed'))[0]?.message ??
              t('gameAccountTools.link.failed')),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gc-bulk-link-title"
        className="flex max-h-full w-full max-w-lg flex-col rounded-card border border-subtle bg-raised shadow-2xl"
      >
        <header className="border-b border-subtle p-5">
          <h2 id="gc-bulk-link-title" className="text-lg font-semibold text-content">
            {t('gameAccountTools.link.title', { count: accounts.length })}
          </h2>
          <p className="mt-1 text-sm text-content-muted">{t('gameAccountTools.link.intro')}</p>
        </header>

        <div className="min-h-0 flex-1 space-y-4 overflow-auto p-5">
          <Field label={t('gameAccountTools.link.game')}>
            <select
              value={gameId}
              onChange={(event) => setGameId(event.target.value)}
              required
              className={inputClass}
            >
              <option value="">{t('gameAccountTools.link.chooseGame')}</option>
              {(games.data ?? []).map((game) => (
                <option key={game.id} value={game.id}>
                  {game.title}
                </option>
              ))}
            </select>
          </Field>

          {alreadyOwn > 0 ? (
            <p className="text-sm text-content-secondary">
              {t('gameAccountTools.link.alreadyOwn', { count: alreadyOwn })}
            </p>
          ) : null}

          <ul className="max-h-48 list-none space-y-1 overflow-auto rounded-control border border-subtle bg-input p-3 text-sm text-content-secondary">
            {accounts.map((account) => (
              <li key={account.id} className="truncate">
                {account.accountName}
              </li>
            ))}
          </ul>
        </div>

        <footer className="border-t border-subtle p-5">
          {error ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {error}
            </p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={onClose} className={ghostPillClass}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting || !gameId} className={pillClass}>
              {t('gameAccountTools.link.apply', { count: accounts.length })}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
